import type React from "react";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { createMaskAdapter } from "./adapters";
import type { MaskInputProps } from "./types";

const MaskInput = forwardRef<HTMLInputElement, MaskInputProps>(
  ({ mask, preset, onChange, className, prefix, suffix, selectAllOnFocus = true, value, ...props }, ref) => {
    const [maskedValue, setMaskedValue] = useState(value?.toString() || "");

    // Create adapter instance based on preset or mask
    const adapter = useMemo(() => {
      return createMaskAdapter(preset, mask);
    }, [preset, mask]);

    // Handle controlled component updates when value changes
    useEffect(() => {
      if (value !== undefined) {
        const formattedValue = adapter.handleControlledUpdate(value);
        setMaskedValue(formattedValue);
      }
    }, [value, adapter]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // Handle empty input
        if (inputValue === "" || adapter.isEmpty(inputValue)) {
          const emptyValue = adapter.handleEmptyValue();
          setMaskedValue(emptyValue);
          onChange?.("", emptyValue);
          return;
        }

        // Parse input using adapter
        const result = adapter.parseFromInput(inputValue);
        setMaskedValue(result.formatted);
        onChange?.(result.value, result.formatted);
      },
      [adapter, onChange],
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (selectAllOnFocus) {
          e.currentTarget.select();
        }
        props.onFocus?.(e);
      },
      [selectAllOnFocus, props.onFocus],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Allow modifier key combinations (Ctrl+A, Cmd+C, etc.)
        if (e.ctrlKey || e.metaKey || e.altKey) {
          props.onKeyDown?.(e);
          return;
        }

        if (!adapter.isKeyAllowed(e.key, maskedValue, e.currentTarget.selectionStart)) {
          e.preventDefault();
          return;
        }

        props.onKeyDown?.(e);
      },
      [adapter, maskedValue, props.onKeyDown],
    );

    // Render with prefix and/or suffix if provided
    if (prefix || suffix) {
      return (
        <div className="relative">
          {prefix && (
            <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
              <span className="text-sm font-medium">{prefix}</span>
            </div>
          )}
          <Input
            ref={ref}
            {...props}
            value={maskedValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            className={cn("peer", prefix && "ps-8", suffix && "pe-12", className)}
          />
          {suffix && (
            <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 peer-disabled:opacity-50">
              <span className="text-sm font-medium">{suffix}</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <Input
        ref={ref}
        {...props}
        value={maskedValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        className={cn(className)}
      />
    );
  },
);

MaskInput.displayName = "InputMask";

export { MaskInput as InputMask };
